import { useState, useRef, type FC, type DragEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AgentType } from '@paladin-monorepo/common';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { llmChatApi } from '@/lib/api/llm-chat';
import { llmChatSessionsQueryKey } from '@/lib/llm-chat-sessions-query';
import { useLlmChatDrawer } from '@/components/llm-chat/llm-chat-drawer-context';
import { LlmChatAttachmentChip } from '@/components/llm-chat/LlmChatAttachmentChip';
import { LLM_CHAT_ATTACHMENT_ACCEPT } from '@/lib/llm-chat-attachment-file-types';
import { Logo } from '@/components/Logo';
import { Notifications } from '@/components/Notifications/Notifications';
import { SidebarAiIcon, SidebarMenuIcon } from '@/components/sidebar/sidebar-icons';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import attachmentIcon from '@/assets/dashboard/attachment.svg';
import monomoAiMark from '@/assets/dashboard/monomo-ai-mark.svg';
import askArrowIcon from '@/assets/dashboard/ask-arrow.svg';
import { BotIcon } from 'lucide-react';

type DashboardHeroSectionProps = {
  welcomeName?: string;
  honoraryBadge?: ReactNode;
  agentType?: AgentType;
  /** Replaces the default inquirer suggestions. */
  suggestions?: string[];
  className?: string;
};

export const DashboardHeroSection: FC<DashboardHeroSectionProps> = ({
  welcomeName,
  honoraryBadge,
  agentType = AgentType.InquirerGuide,
  suggestions: customSuggestions,
  className,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openChat } = useLlmChatDrawer();
  const { setOpenMobile } = useSidebar();
  const [query, setQuery] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const suggestions = customSuggestions ?? [
    t('dashboard.inquirer.widgets.ai.suggestion1'),
    t('dashboard.inquirer.widgets.ai.suggestion2'),
    t('dashboard.inquirer.widgets.ai.suggestion3'),
  ];

  const startSession = async (text: string) => {
    const trimmed = text.trim();
    if ((!trimmed && pendingFiles.length === 0) || loading) return;
    setLoading(true);
    try {
      const session = await llmChatApi.createSession({ agentType });

      let attachmentIds: string[] | undefined;
      if (pendingFiles.length > 0) {
        try {
          const uploads = await Promise.all(
            pendingFiles.map((file) => llmChatApi.uploadAttachment(session.id, file)),
          );
          attachmentIds = uploads.map((a) => a.id);
        } catch {
          toast.error(t('dashboard.inquirer.widgets.ai.uploadError'));
          return;
        }
      }

      setPendingFiles([]);
      await queryClient.prefetchQuery({
        queryKey: llmChatSessionsQueryKey(),
        queryFn: () => llmChatApi.getSessions(),
      });
      openChat({
        sessionId: session.id,
        initialMessage: trimmed,
        ...(attachmentIds && attachmentIds.length > 0 ? { initialAttachmentIds: attachmentIds } : {}),
      });
    } catch {
      toast.error(t('dashboard.inquirer.widgets.ai.startError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (loading) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setPendingFiles((prev) => [...prev, ...files]);
    }
  };

  const renderAiSidebarButton = () => (
    <button
      type="button"
      className="flex size-9 shrink-0 items-center justify-center rounded-md border border-white/50 text-white hover:bg-white/10"
      aria-label={t('llmChat.openAssistant')}
      title={t('llmChat.openAssistant')}
      onClick={() => openChat()}
    >
      {/* <SidebarAiIcon className="size-5" /> */}
      <BotIcon className="size-5" />
    </button>
  );

  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-t-sm bg-block-ink px-5 pb-16 pt-5 text-white shadow-[0px_1px_2px_0px_rgba(6,35,54,0.04)] sm:px-6 sm:pb-20 md:rounded-none',
        className,
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="mb-6 flex items-center justify-between md:hidden">
        <Logo
          noName={false}
          className="gap-2 [&_svg]:size-7 [&_svg]:fill-white [&_span]:font-display [&_span]:text-base [&_span]:text-white"
        />
        <div className="flex items-center gap-2.5">
          <Notifications variant="sidebar" />
          {renderAiSidebarButton()}
          <button
            type="button"
            className="flex size-9 items-center justify-center rounded-md border border-white/50 text-white hover:bg-white/10"
            aria-label={t('sidebar.openMenu')}
            onClick={() => setOpenMobile(true)}
          >
            <SidebarMenuIcon className="size-5" />
          </button>
        </div>
      </div>

      <div className="absolute right-5 top-5 z-10 hidden md:block sm:right-6">
        {renderAiSidebarButton()}
      </div>

      {welcomeName && (
        <div className="mb-12 flex flex-wrap items-center gap-3 md:pr-14">
          <h1 className="text-display-1 text-brand-secondary tracking-tight">
            <span className="font-display">{t('dashboard.welcome')},</span>{' '}
            <span className="font-sans font-normal">{welcomeName}</span>
          </h1>
          {honoraryBadge}
        </div>
      )}

      <div className="mb-4">
        <div className="mb-1.5 flex items-center gap-2">
          <img
            src={monomoAiMark}
            alt=""
            className="size-9 shrink-0"
            width={36}
            height={36}
          />
          <span className="text-display-2 text-brand-secondary-bright tracking-tight">
            Monomo
          </span>
          <span className="text-display-1 text-brand-secondary-bright tracking-[-0.067em]">
            AI
          </span>
        </div>
        <p className="w-full text-[12px] leading-normal text-white">
          {t('dashboard.inquirer.widgets.ai.subtitle')}
        </p>
      </div>

      {isDragging && (
        <div className="mb-4 flex items-center justify-center rounded-md border-2 border-dashed border-brand-secondary/50 bg-brand-secondary/10 px-4 py-6 text-sm text-brand-secondary-bright">
          {t('llmChat.dropFilesHere')}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={LLM_CHAT_ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={handleFileSelect}
      />

      {pendingFiles.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {pendingFiles.map((file, i) => (
            <LlmChatAttachmentChip
              key={`${file.name}-${i}`}
              name={file.name}
              size={file.size}
              onRemove={() => removeFile(i)}
            />
          ))}
        </div>
      )}

      <div className="flex h-12 items-center gap-2 rounded-lg border border-border-strong bg-white px-1.5 pl-2.5 dark:border-[#2e3236] dark:bg-[#131313]">
        <button
          type="button"
          className="flex size-[22px] shrink-0 rotate-45 items-center justify-center overflow-clip"
          aria-label={t('dashboard.inquirer.widgets.ai.attachAria')}
          disabled={loading}
          onClick={() => fileInputRef.current?.click()}
        >
          <img src={attachmentIcon} alt="" className="size-4 -rotate-45" width={16} height={16} />
        </button>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void startSession(query);
            }
          }}
          placeholder={t('dashboard.inquirer.widgets.ai.placeholder')}
          className="h-full min-w-0 flex-1 bg-transparent text-body text-foreground outline-none placeholder:text-text-subtle dark:text-white dark:placeholder:text-text-subtle"
          aria-label={t('dashboard.inquirer.widgets.ai.placeholder')}
          disabled={loading}
        />
        <Button
          type="button"
          className="h-[35px] shrink-0 rounded-sm bg-brand-primary px-5 font-label text-white hover:bg-brand-primary-dark"
          aria-label={t('dashboard.inquirer.widgets.ai.submitAria')}
          disabled={loading}
          onClick={() => void startSession(query)}
        >
          {t('dashboard.inquirer.widgets.ai.ask')}
          <span className="relative size-3.5 overflow-clip">
            <img src={askArrowIcon} alt="" className="size-full" width={14} height={14} />
          </span>
        </Button>
      </div>

      <div className="mt-3.5 flex flex-wrap gap-x-4 gap-y-2">
        {suggestions.map((label) => (
          <button
            key={label}
            type="button"
            className="text-body text-surface-alt underline decoration-solid underline-offset-2 hover:text-white disabled:opacity-50 dark:text-surface-alt dark:hover:text-white"
            disabled={loading}
            onClick={() => void startSession(label)}
          >
            {label}
          </button>
        ))}
      </div>
    </section>
  );
};
